import { DmChannelSettingValue, GdmChannelSettingValue, SpaceChannelSettingValue, StartAuthenticationResponseSchema, FinishAuthenticationResponseSchema, GetSettingsResponseSchema, DmChannelSettingSchema, GdmChannelSettingSchema, SpaceSettingSchema, SpaceChannelSettingSchema, } from '@towns-protocol/proto';
import { NotificationService, userIdFromAddress, makeNotificationRpcClient, Observable, spaceIdFromChannelId, streamIdAsBytes, streamIdAsString, isDMChannelStreamId, isGDMChannelStreamId, } from '@towns-protocol/sdk';
import { create, fromBinary, toBinary, toJson } from '@bufbuild/protobuf';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useTownsContext } from '../components/TownsContextProvider';
import { bin_fromBase64, bin_toBase64 } from '@towns-protocol/dlog';
import cloneDeep from 'lodash/cloneDeep';
class LocalStorageNotificationStore {
    getItem(schema, key) {
        const data = localStorage.getItem(key);
        if (!data) {
            return undefined;
        }
        try {
            return fromBinary(schema, bin_fromBase64(data));
        }
        catch (error) {
            console.error('TNS PUSH: error parsing local storage', error);
            return undefined;
        }
    }
    setItem(schema, key, value) {
        const bytes = toBinary(schema, value);
        localStorage.setItem(key, bin_toBase64(bytes));
    }
}
const emptyNotificationSettingsModel = {
    fetchedAtMs: undefined,
    settings: undefined,
    error: undefined,
};
export class NotificationSettingsClient {
    signerContext;
    environmnetId;
    url;
    opts;
    store;
    data;
    _client;
    startResponseKey;
    finishResponseKey;
    settingsKey;
    getClientPromise;
    getSettingsPromise;
    constructor(signerContext, environmnetId, url, opts = undefined, store = new LocalStorageNotificationStore()) {
        this.signerContext = signerContext;
        this.environmnetId = environmnetId;
        this.url = url;
        this.opts = opts;
        this.store = store;
        const keyPrefix = `RNS-${environmnetId}-${this.userId}`;
        this.startResponseKey = `${keyPrefix}-startResponse`;
        this.finishResponseKey = `${keyPrefix}-finishResponse`;
        this.settingsKey = `${keyPrefix}-settings`;
        this.data = new Observable({
            fetchedAtMs: undefined,
            settings: this.getLocalSettings(),
            error: undefined,
        });
    }
    get userId() {
        return userIdFromAddress(this.signerContext.creatorAddress);
    }
    getLocalStartResponse() {
        return this.store.getItem(StartAuthenticationResponseSchema, this.startResponseKey);
    }
    setLocalStartResponse(response) {
        this.store.setItem(StartAuthenticationResponseSchema, this.startResponseKey, response);
    }
    getLocalFinishResponse() {
        return this.store.getItem(FinishAuthenticationResponseSchema, this.finishResponseKey);
    }
    setLocalFinishResponse(response) {
        this.store.setItem(FinishAuthenticationResponseSchema, this.finishResponseKey, response);
    }
    getLocalSettings() {
        return this.store.getItem(GetSettingsResponseSchema, this.settingsKey);
    }
    setLocalSettings(settings) {
        this.store.setItem(GetSettingsResponseSchema, this.settingsKey, settings);
    }
    updateLocalSettings(fn) {
        if (!this.data.value.settings) {
            throw new Error('TNS PUSH: settings has not been fetched');
        }
        const newSettings = cloneDeep(this.data.value.settings); // aellis we probably don't need a clone deep
        fn(newSettings);
        this.setLocalSettings(newSettings);
        this.data.setValue({ ...this.data.value, settings: newSettings });
    }
    async getClient() {
        if (this.getClientPromise) {
            return this.getClientPromise;
        }
        try {
            this.getClientPromise = this._getClient();
            const result = await this.getClientPromise;
            this.getClientPromise = undefined;
            return result;
        }
        catch (error) {
            this.data.setValue({ ...this.data.value, error: error });
            this.getClientPromise = undefined;
            return undefined;
        }
    }
    async _getClient() {
        if (!this.url) {
            throw new Error('TNS PUSH: notification service url is unset');
        }
        const startResponse = this.getLocalStartResponse();
        const finishResponse = this.getLocalFinishResponse();
        // if we have a valid start response and finish response, and the start response is still valid,
        // we can re-create the client from the local storage
        // and cache it locally to leverage http2 connection pooling
        if (startResponse &&
            finishResponse &&
            startResponse.expiration &&
            startResponse.expiration.seconds > Date.now() / 1000) {
            if (this._client) {
                // if everything is still valid, and we have the client, return the client
                return this._client;
            }
            try {
                const client = makeNotificationRpcClient(this.url, finishResponse.sessionToken, this.opts);
                this._client = client;
                return client;
            }
            catch (error) {
                console.error('TNS PUSH: error authenticating from local storage, will try from scratch', error);
            }
        }
        // if we don't have a valid client, or if the client has expired,we need to authenticate from scratch
        const service = await NotificationService.authenticate(this.signerContext, this.url, this.opts);
        this.setLocalStartResponse(service.startResponse);
        this.setLocalFinishResponse(service.finishResponse);
        return service.notificationRpcClient;
    }
    async withClient(fn) {
        const client = await this.getClient();
        if (client) {
            try {
                return await fn(client);
            }
            catch (error) {
                this.data.setValue({ ...this.data.value, error: error });
                throw error;
            }
        }
        // either an error is already logged or the initialize threw an error
        return undefined;
    }
    async getSettings() {
        if (this.getSettingsPromise) {
            return this.getSettingsPromise;
        }
        this.getSettingsPromise = this._getSettings();
        return this.getSettingsPromise;
    }
    async _getSettings() {
        return this.withClient(async (client) => {
            try {
                const response = await client.getSettings({});
                this.setLocalSettings(response);
                this.data.setValue({
                    fetchedAtMs: Date.now(),
                    settings: response,
                    error: undefined,
                });
                console.log('TNS PUSH: fetched settings', toJson(GetSettingsResponseSchema, response));
                this.getSettingsPromise = undefined;
                return response;
            }
            catch (error) {
                this.data.setValue({
                    ...this.data.value,
                    error: error,
                });
                throw error;
            }
        });
    }
    async subscribeWebPush(subscription) {
        return this.withClient(async (client) => {
            console.log('TNS PUSH: subscribing to web push');
            return client.subscribeWebPush({ subscription });
        });
    }
    async unsubscribeWebPush(subscription) {
        return this.withClient(async (client) => {
            console.log('TNS PUSH: unsubscribing to web push');
            return client.unsubscribeWebPush({ subscription });
        });
    }
    async setDmGlobalSetting(value) {
        return this.withClient(async (client) => {
            await client.setDmGdmSettings({
                dmGlobal: value,
                gdmGlobal: this.data.value.settings?.gdmGlobal ?? GdmChannelSettingValue.GDM_UNSPECIFIED,
            });
            this.updateLocalSettings((settings) => {
                settings.dmGlobal = value;
            });
        });
    }
    async setGdmGlobalSetting(value) {
        return this.withClient(async (client) => {
            await client.setDmGdmSettings({
                dmGlobal: this.data.value.settings?.dmGlobal ?? DmChannelSettingValue.DM_UNSPECIFIED,
                gdmGlobal: value,
            });
            this.updateLocalSettings((settings) => {
                settings.gdmGlobal = value;
            });
        });
    }
    async setDmChannelSetting(channelId, value) {
        return this.withClient(async (client) => {
            await client.setDmChannelSetting({
                dmChannelId: streamIdAsBytes(channelId),
                value,
            });
            this.updateLocalSettings((settings) => {
                settings.dmChannels = settings.dmChannels.filter((c) => streamIdAsString(c.channelId) !== channelId);
                settings.dmChannels.push(create(DmChannelSettingSchema, {
                    channelId: streamIdAsBytes(channelId),
                    value,
                }));
            });
        });
    }
    async setGdmChannelSetting(channelId, value) {
        return this.withClient(async (client) => {
            await client.setGdmChannelSetting({
                gdmChannelId: streamIdAsBytes(channelId),
                value,
            });
            this.updateLocalSettings((settings) => {
                settings.gdmChannels = settings.gdmChannels.filter((c) => streamIdAsString(c.channelId) !== channelId);
                settings.gdmChannels.push(create(GdmChannelSettingSchema, {
                    channelId: streamIdAsBytes(channelId),
                    value,
                }));
            });
        });
    }
    async setSpaceSetting(spaceId, value) {
        return this.withClient(async (client) => {
            await client.setSpaceSettings({ spaceId: streamIdAsBytes(spaceId), value });
            this.updateLocalSettings((settings) => {
                const spaceIndex = settings.space.findIndex((s) => streamIdAsString(s.spaceId) === spaceId);
                if (spaceIndex != -1) {
                    settings.space[spaceIndex].value = value;
                }
                else {
                    settings.space.push(create(SpaceSettingSchema, {
                        spaceId: streamIdAsBytes(spaceId),
                        value,
                        channels: [],
                    }));
                }
            });
        });
    }
    async setChannelSetting(channelId, value) {
        const spaceId = spaceIdFromChannelId(channelId);
        return this.withClient(async (client) => {
            await client.setSpaceChannelSettings({
                spaceId: streamIdAsBytes(spaceId),
                channelId: streamIdAsBytes(channelId),
                value,
            });
            this.updateLocalSettings((settings) => {
                let spaceIndex = settings.space.findIndex((s) => streamIdAsString(s.spaceId) === spaceId);
                if (spaceIndex == -1) {
                    spaceIndex = settings.space.length;
                    settings.space.push(create(SpaceSettingSchema, {
                        spaceId: streamIdAsBytes(spaceId),
                        value: SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_UNSPECIFIED,
                        channels: [],
                    }));
                }
                const channelIndex = settings.space[spaceIndex].channels.findIndex((c) => streamIdAsString(c.channelId) === channelId);
                if (channelIndex == -1) {
                    settings.space[spaceIndex].channels.push(create(SpaceChannelSettingSchema, {
                        channelId: streamIdAsBytes(channelId),
                        value,
                    }));
                }
                else {
                    settings.space[spaceIndex].channels[channelIndex].value = value;
                }
            });
        });
    }
}
// safe to call from any component, as many times as needed
export function useNotificationSettings() {
    const { notificationSettingsClient: client } = useTownsContext();
    useEffect(() => {
        // fetch once if not up to date
        if (client &&
            (!client.data.value.fetchedAtMs ||
                client.data.value.fetchedAtMs < Date.now() - 1000 * 60 * 60 * 5)) {
            void client?.getSettings();
        }
    }, [client]);
    const subscribeFn = useCallback((subFn) => {
        if (!client) {
            return () => { };
        }
        return client?.data.subscribe(subFn);
    }, [client]);
    const data = useSyncExternalStore(subscribeFn, () => client?.data.value ?? emptyNotificationSettingsModel);
    const getSpaceSetting = useCallback((spaceId) => {
        const spaceSettings = data.settings?.space.find((s) => streamIdAsString(s.spaceId) === spaceId);
        if (spaceSettings &&
            spaceSettings.value !== SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_UNSPECIFIED) {
            // if space is set
            return spaceSettings.value;
        }
        // default
        return SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_ONLY_MENTIONS_REPLIES_REACTIONS;
    }, [data.settings?.space]);
    const getChannelSetting = useCallback((channelId) => {
        const spaceId = spaceIdFromChannelId(channelId);
        const spaceSetting = data.settings?.space.find((s) => streamIdAsString(s.spaceId) === spaceId);
        if (spaceSetting) {
            const channelSetting = spaceSetting.channels.find((c) => streamIdAsString(c.channelId) === channelId);
            // if channel is set
            if (channelSetting &&
                channelSetting.value !==
                    SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_UNSPECIFIED) {
                return channelSetting.value;
            }
            // if space is set
            if (spaceSetting.value !==
                SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_UNSPECIFIED) {
                return spaceSetting.value;
            }
        }
        // default
        return SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_ONLY_MENTIONS_REPLIES_REACTIONS;
    }, [data.settings?.space]);
    const getRawChannelSetting = useCallback((channelId) => {
        const spaceId = spaceIdFromChannelId(channelId);
        const spaceSetting = data.settings?.space.find((s) => streamIdAsString(s.spaceId) === spaceId);
        if (spaceSetting) {
            const channelSetting = spaceSetting.channels.find((c) => streamIdAsString(c.channelId) === channelId);
            // if channel is set
            if (channelSetting) {
                return channelSetting.value;
            }
        }
        return SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_UNSPECIFIED;
    }, [data.settings]);
    const getDmGlobalSetting = useCallback(() => {
        if (data.settings && data.settings.dmGlobal !== DmChannelSettingValue.DM_UNSPECIFIED) {
            return data.settings?.dmGlobal;
        }
        return DmChannelSettingValue.DM_MESSAGES_YES;
    }, [data.settings]);
    const getDmSetting = useCallback((channelId) => {
        const dmSetting = data.settings?.dmChannels.find((c) => streamIdAsString(c.channelId) === channelId);
        if (dmSetting && dmSetting.value !== DmChannelSettingValue.DM_UNSPECIFIED) {
            return dmSetting.value;
        }
        // global
        if (data.settings && data.settings?.dmGlobal !== DmChannelSettingValue.DM_UNSPECIFIED) {
            return data.settings.dmGlobal;
        }
        // default
        return DmChannelSettingValue.DM_MESSAGES_YES;
    }, [data.settings]);
    const getRawDmSetting = useCallback((channelId) => {
        const dmSetting = data.settings?.dmChannels.find((c) => streamIdAsString(c.channelId) === channelId);
        return dmSetting?.value ?? DmChannelSettingValue.DM_UNSPECIFIED;
    }, [data.settings]);
    const getGdmGlobalSetting = useCallback(() => {
        if (data.settings && data.settings.gdmGlobal !== GdmChannelSettingValue.GDM_UNSPECIFIED) {
            return data.settings?.gdmGlobal;
        }
        return GdmChannelSettingValue.GDM_MESSAGES_ALL;
    }, [data.settings]);
    const getGdmSetting = useCallback((channelId) => {
        const gdmSetting = data.settings?.gdmChannels.find((c) => streamIdAsString(c.channelId) === channelId);
        if (gdmSetting && gdmSetting.value !== GdmChannelSettingValue.GDM_UNSPECIFIED) {
            return gdmSetting.value;
        }
        // global
        if (data.settings &&
            data.settings.gdmGlobal !== GdmChannelSettingValue.GDM_UNSPECIFIED) {
            return data.settings.gdmGlobal;
        }
        // default
        return GdmChannelSettingValue.GDM_MESSAGES_ALL;
    }, [data.settings]);
    const getRawGdmSetting = useCallback((channelId) => {
        const gdmSetting = data.settings?.gdmChannels.find((c) => streamIdAsString(c.channelId) === channelId);
        return gdmSetting?.value ?? GdmChannelSettingValue.GDM_UNSPECIFIED;
    }, [data.settings]);
    return {
        isLoading: !data.fetchedAtMs,
        error: data.error,
        notificationSettingsClient: client,
        settings: data.settings,
        getSpaceSetting,
        getChannelSetting,
        getRawChannelSetting,
        getDmSetting,
        getRawDmSetting,
        getGdmSetting,
        getRawGdmSetting,
        getDmGlobalSetting,
        getGdmGlobalSetting,
    };
}
export function useMutedStreamIds() {
    const { settings } = useNotificationSettings();
    return useMemo(() => {
        return getMutedChannelIds(settings) ?? new Set();
    }, [settings]);
}
export function getMutedChannelIds(settings) {
    if (!settings) {
        return undefined;
    }
    const ids = new Set();
    for (const spaceSetting of settings.space) {
        if (spaceSetting.value ===
            SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_NO_MESSAGES_AND_MUTE) {
            ids.add(streamIdAsString(spaceSetting.spaceId));
        }
        for (const channelSetting of spaceSetting.channels) {
            if (channelSetting.value ===
                SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_NO_MESSAGES_AND_MUTE) {
                ids.add(streamIdAsString(channelSetting.channelId));
            }
        }
    }
    for (const dmSetting of settings.dmChannels) {
        if (dmSetting.value === DmChannelSettingValue.DM_MESSAGES_NO_AND_MUTE) {
            ids.add(streamIdAsString(dmSetting.channelId));
        }
    }
    for (const gdmSetting of settings.gdmChannels) {
        if (gdmSetting.value === GdmChannelSettingValue.GDM_MESSAGES_NO_AND_MUTE) {
            ids.add(streamIdAsString(gdmSetting.channelId));
        }
    }
    return ids;
}
export function useMuteSettings({ spaceId, channelId, }) {
    const { getChannelSetting, getSpaceSetting, getDmSetting, getGdmSetting } = useNotificationSettings();
    return useMemo(() => {
        try {
            if (channelId && isDMChannelStreamId(channelId)) {
                return {
                    channelIsMuted: getDmSetting(channelId) === DmChannelSettingValue.DM_MESSAGES_NO_AND_MUTE,
                    spaceIsMuted: false,
                };
            }
            else if (channelId && isGDMChannelStreamId(channelId)) {
                return {
                    channelIsMuted: getGdmSetting(channelId) ===
                        GdmChannelSettingValue.GDM_MESSAGES_NO_AND_MUTE,
                    spaceIsMuted: false,
                };
            }
            return {
                channelIsMuted: channelId
                    ? getChannelSetting(channelId) ===
                        SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_NO_MESSAGES_AND_MUTE
                    : false,
                spaceIsMuted: spaceId
                    ? getSpaceSetting(spaceId) ===
                        SpaceChannelSettingValue.SPACE_CHANNEL_SETTING_NO_MESSAGES_AND_MUTE
                    : false,
            };
        }
        catch (error) {
            return {
                channelIsMuted: false,
                spaceIsMuted: false,
            };
        }
    }, [channelId, getChannelSetting, spaceId, getSpaceSetting, getDmSetting, getGdmSetting]);
}
//# sourceMappingURL=TownsNotifciationSettings.js.map